// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Condicional.tsx
//  Listagem de Vendas Condicionais
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Trash2, Package, Clock, CheckCircle2,
  Eye, Pencil, ChevronLeft, ChevronRight, RotateCcw, AlertCircle, CalendarX,
} from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { useCondicional } from '@/features/operacional/hooks/useCondicional';
import { calcularStatusCondicional } from '@/features/operacional/types';
import type { CondicionalResumo, StatusCondicional } from '@/features/operacional/types';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const statusConfig: Record<StatusCondicional, { label: string; className: string; icon: typeof Clock }> = {
  aberta:   { label: 'Aberta',    className: 'bg-blue-500/10 text-blue-500',    icon: Clock        },
  vendida:  { label: 'Vendida',   className: 'bg-green-500/10 text-green-500',  icon: CheckCircle2 },
  devolvida:{ label: 'Devolvida', className: 'bg-gray-500/10 text-gray-500',    icon: RotateCcw    },
  vencida:  { label: 'Vencida',   className: 'bg-orange-500/10 text-orange-500',icon: AlertCircle  },
  expirada: { label: 'Expirada',  className: 'bg-slate-500/10 text-slate-500',  icon: CalendarX    },
};

const PAGE_SIZES = [10, 20, 50, 100] as const;

const Condicional = () => {
  const navigate = useNavigate();
  const [searchTerm,    setSearchTerm]    = useState('');
  const [statusFiltro,  setStatusFiltro]  = useState<string>('todos');
  const [deletingId,    setDeletingId]    = useState<number | null>(null);
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);

  // Abre nos últimos 30 dias, mais recentes primeiro: com o tempo a lista inteira fica grande demais.
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<'emissao' | 'numero' | 'cliente' | 'validade' | 'valor'>('emissao', true, () => setPagina(1));
  // Busca e status vão ao servidor (antes filtravam só a página carregada): espera a digitação parar.
  const buscaEnviada = useDebounce(searchTerm.trim(), 400);

  const { condicionais, total, totalPaginas, resumo, vencidas, isLoading, isFetching,
          deleteCondicional, expirarVencidas, isExpirando } =
    useCondicional({
      pagina, tamanhoPagina,
      filtros: {
        busca: buscaEnviada, status: statusFiltro, ...periodo.intervalo,
        ordenarPor: ordenacao.ordenarPor, decrescente: ordenacao.decrescente,
      },
    });

  // KPIs do servidor: busca + período inteiros, não só a página.
  const totalAbertas    = resumo?.abertas     ?? 0;
  const totalVendidas   = resumo?.vendidas    ?? 0;
  const totalDevolvidas = resumo?.devolvidas  ?? 0;
  const valorAberto     = resumo?.valorAberto ?? 0;

  const firstItem = (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);

  const handlePageSizeChange = (value: string) => {
    setTamanhoPagina(Number(value)); setPagina(1);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Venda Condicional</h1>
          <p className="text-muted-foreground mt-1">Gerencie as vendas condicionais (produtos em consignação)</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/operacional/condicional/novo')}>
          <Plus className="h-4 w-4" />Nova Condicional
        </Button>
      </div>

      {/* Alerta de condicionais vencidas — saldo preso no estoque (achado EST-05) */}
      {vencidas.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-orange-500/30 bg-orange-500/10 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-foreground">
                {vencidas.length} condicional(is) vencida(s) com saldo retido no estoque
              </p>
              <p className="text-sm text-muted-foreground">
                A validade passou e a mercadoria ainda consta em poder do cliente, reduzindo o disponível
                para venda. Faça o acerto/devolução manual ou expire as vencidas (devolve o saldo ao depósito).
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            className="gap-2 shrink-0 border-orange-500/40"
            disabled={isExpirando}
            onClick={() => expirarVencidas()}
          >
            <CalendarX className="h-4 w-4" />
            {isExpirando ? 'Expirando...' : 'Expirar vencidas'}
          </Button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />Abertas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{totalAbertas}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {valorAberto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />Vendidas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{totalVendidas}</div>
            <p className="text-xs text-muted-foreground mt-1">Confirmadas</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <RotateCcw className="h-4 w-4" />Devolvidas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-500">{totalDevolvidas}</div>
            <p className="text-xs text-muted-foreground mt-1">Retornadas ao estoque</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Package className="h-4 w-4" />Total Geral
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{total}</div>
            <p className="text-xs text-muted-foreground mt-1">condicionais no filtro</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <CardTitle>Listagem de Condicionais</CardTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap md:justify-end gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Nº, cliente ou CPF/CNPJ..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPagina(1); }}
                  className="pl-9" />
              </div>
              <PeriodoFiltro estado={periodo} rotuloData="Emissão" />
              <Select value={statusFiltro} onValueChange={(v) => { setStatusFiltro(v); setPagina(1); }}>
                <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="aberta">Aberta</SelectItem>
                  <SelectItem value="vendida">Vendida</SelectItem>
                  <SelectItem value="devolvida">Devolvida</SelectItem>
                  <SelectItem value="vencida">Vencida</SelectItem>
                  <SelectItem value="expirada">Expirada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Nº</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="cliente" ordenacao={ordenacao} decrescentePadrao={false}>Cliente</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="emissao" ordenacao={ordenacao}>Emissão</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="validade" ordenacao={ordenacao}>Validade</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao} className="text-right">Total</CabecalhoOrdenavel>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : condicionais.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {periodo.periodo !== 'todos'
                      ? 'Nenhuma condicional neste período — amplie o período para ver as anteriores'
                      : 'Nenhuma condicional encontrada'}
                  </TableCell></TableRow>
                ) : (
                  condicionais.map((c: CondicionalResumo) => {
                    const status = calcularStatusCondicional(c.isBaixada, c.isDevolvido, c.dtValidade, c.isExpirada);
                    const cfg    = statusConfig[status];
                    const Icon   = cfg.icon;
                    const podeCriar = !c.isBaixada && !c.isDevolvido;
                    return (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">#{c.numeroOrcamento}</TableCell>
                        <TableCell>{c.nomeRazao || <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell>{new Date(c.dtEmissao).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell>{new Date(c.dtValidade).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell className="text-right font-medium">
                          {(c.totalLiquido ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </TableCell>
                        <TableCell>
                          <Badge className={cfg.className}>
                            <span className="flex items-center gap-1"><Icon className="h-3 w-3" />{cfg.label}</span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {podeCriar && (
                              <Button size="sm" variant="ghost"
                                onClick={() => navigate(`/operacional/condicional/${c.id}`)} title="Editar">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" title="Ver detalhes"
                              onClick={() => navigate(`/operacional/condicional/${c.id}`)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            {podeCriar && (
                              <Button size="sm" variant="ghost" onClick={() => setDeletingId(c.id)} title="Excluir">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0 ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}><ChevronLeft className="h-4 w-4" /></Button>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)         page = i + 1;
                  else if (pagina <= 3)                page = i + 1;
                  else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
                  else                                 page = pagina - 2 + i;
                  return (
                    <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onConfirm={() => { if (deletingId) { deleteCondicional(deletingId); setDeletingId(null); } }}
        title="Excluir Condicional"
        description="Tem certeza que deseja excluir esta venda condicional?"
      />
    </div>
  );
};

export default Condicional;
